import { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Brain, TrendingUp, TrendingDown, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { MAINTENANCE_MODE } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeframes = [
  { value: '1hr', label: '1 Hour' },
  { value: '24hr', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '1w', label: '1 Week' },
  { value: '1m', label: '1 Month' },
  { value: '3m', label: '3 Months' }
];

const PredictionsPanel = ({ predictions, onRefresh, currentPrice }) => {
  const [generating, setGenerating] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('24hr');

  const generatePrediction = async () => {
    if (MAINTENANCE_MODE) {
      toast.error('AI predictions are temporarily disabled during maintenance.');
      return;
    }
    
    setGenerating(true);
    try {
      await axios.post(`${API}/predictions/generate`, { timeframe: selectedTimeframe });
      toast.success('Prediction generated successfully!');
      onRefresh();
    } catch (error) {
      console.error('Error generating prediction:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate prediction');
    } finally {
      setGenerating(false);
    }
  };

  const getConfidenceColor = (confidence) => {
    const colors = {
      High: 'text-gain bg-gain-soft border-gain/20',
      Medium: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/30',
      Low: 'text-loss bg-loss-soft border-loss/20'
    };
    return colors[confidence] || colors.Medium;
  };

  return (
    <Card className="p-6 h-full flex flex-col">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-heading font-semibold tracking-tight">AI Predictions</h3>
          <p className="text-xs text-muted-foreground">Powered by Gemini</p>
        </div>
      </div>

      {/* Timeframe Selector */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {timeframes.map((tf) => (
          <button
            key={tf.value}
            data-testid={`timeframe-${tf.value}`}
            onClick={() => setSelectedTimeframe(tf.value)}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              selectedTimeframe === tf.value
                ? 'bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 ring-2 ring-violet-200 dark:ring-violet-900'
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            {tf.label}
          </button>
        ))}
      </div>

      <Button
        data-testid="generate-prediction-btn"
        onClick={generatePrediction}
        disabled={generating || MAINTENANCE_MODE}
        className="w-full mb-6 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {MAINTENANCE_MODE ? (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            Predictions Unavailable
          </>
        ) : generating ? (
          <>
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Prediction
          </>
        )}
      </Button>

      {/* Predictions List */}
      <div className="flex-1 overflow-y-auto space-y-3">
        <AnimatePresence>
          {predictions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <Brain className="w-12 h-12 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">No predictions yet</p>
              <p className="text-xs text-muted-foreground mt-1">Generate your first AI prediction</p>
            </div>
          ) : (
            predictions.slice(0, 5).map((pred, index) => {
              const isPriceUp = pred.predicted_price > currentPrice;
              return (
                <motion.div
                  key={pred.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 rounded-xl border border-border/50 bg-gradient-to-br from-background to-secondary/30 hover:shadow-md transition-all"
                  data-testid={`prediction-${pred.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {isPriceUp ? (
                        <TrendingUp className="w-4 h-4 text-gain" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-loss" />
                      )}
                      <span className="font-heading font-semibold text-lg">
                        ${pred.predicted_price.toLocaleString()}
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-md border font-medium ${getConfidenceColor(pred.confidence)}`}>
                      {pred.confidence}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{pred.reasoning}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {pred.timeframe}
                    </div>
                    <span>•</span>
                    <span>{pred.target_date}</span>
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>
    </Card>
  );
};

export default PredictionsPanel;